import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useMemo,
  useImperativeHandle,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import TabPanel from "@mui/lab/TabPanel";
import "./SnGrid.css"; // 커스텀 CSS 추가
import { createColumnDefs } from "./columnDefs";
import { ColumnConfig, CustomColDef, FlatRowType } from "./types";
import { menuInfo } from "@shared/type/CommonType";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  ModuleRegistry,
  ExcelExportModule,
  RowGroupingModule,
  ClientSideRowModelModule,
  GridChartsModule,
  ColDef,
  EditableCallbackParams, // 통합 차트 모듈
  GridApi,
  GridOptions,
  ColumnState,
  CellKeyDownEvent,
  Column,
  NavigateToNextCellParams,
  ColGroupDef,
  RowNode,
  CellFocusedEvent,
  RowSelectionOptions,
  GetMainMenuItemsParams,
  MenuItemDef,
  DefaultMenuItem,
} from "ag-grid-enterprise";
import { v4 as uuidv4 } from "uuid";
import { global, useMenuStore } from "@shared/libs/Global";
import { getDate } from "date-fns";
import { showDialog } from "src/widgets/CommonDialog";
import SnTab from "../SnTab";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { showPopupModal } from "@widgets/CommonModal";
import {
  getSearchData,
  getInitPopUp,
  isDateValue,
  isNumeric,
} from "@shared/utils/CommonUtils";
import { StyleSettings } from "../../../styles/StyleSettings";
import SinglePopup from "@widgets/commonpopup/SinglePopup";
import MultiPopup from "@widgets/commonpopup/MultiPopup";
import SnTabHeader from "../SnTabHeader";
import SnFilterSearchBox from "../SnFilterSearchBox";
import PasswordPopup from "@widgets/commonpopup/ResetPwdPopup";
// 필요한 모듈 등록
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ExcelExportModule,
  RowGroupingModule,
  GridChartsModule,
]);

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

//#region 우클릭 메뉴 한글화 작업

const localeText = {
  sortAscending: "오름차순 정렬",
  sortDescending: "내림차순 정렬",

  // 컬럼 크기 조정
  autosizeThisColumn: "이 열 크기 자동 조정",
  autosizeAllColumns: "모든 열 크기 자동 조정",

  // 컬럼 선택
  pivotMode: "피벗 모드",

  toolPanelColumns: "컬럼 선택",

  // 초기화
  resetColumns: "열 초기화",

  // 기타 기본 키
  pinColumn: "컬럼 고정",

  // 컬럼 고정 메뉴
  pinLeft: "왼쪽에 고정", // Pin Left
  pinRight: "오른쪽에 고정", // Pin Right
  noPin: "고정 해제", // No Pin

  csvExport: "CSV 내보내기", // CSV Export
  excelExport: "Excel 내보내기", // Excel Export

  copy: "복사",
  copyWithHeaders: "헤더 포함 복사",
  copyWithGroupHeaders: "그룹 헤더 포함 복사",
  paste: "붙여넣기",
  export: "내보내기",
  filterOoo: "필터...",
  equals: "같음",
  notEqual: "같지 않음",
  lessThan: "미만",
  greaterThan: "초과",
  inRange: "범위 내",
  groupBy: "그룹화",
  expandAll: "전체 확장",
  collapseAll: "전체 축소",
  autoSizeThis: "이 열 크기 자동 조정",
  chartRange: "차트 범위",
  categories: "범주",
  series: "시리즈",
  chartSettings: "차트 설정",
  chartType: "차트 유형",
  legend: "범례",
  axisLabels: "축 레이블",
  valueAxis: "값 축",
  categoryAxis: "범주 축",
  exportChart: "차트 내보내기",

  // 추가 번역 항목
  resetChart: "차트 초기화",
  groupedColumn: "그룹화된 열",
  stackedColumn: "스택 열",
  normalizedColumn: "정규화된 열",
  groupedBar: "그룹화된 막대",
  stackedBar: "스택 막대",
  normalizedBar: "정규화된 막대",
  pie: "원형 차트",
  doughnut: "도넛 차트",
  line: "선형 차트",
  scatter: "산점도",
  bubble: "버블 차트",
  area: "영역 차트",
  stackedArea: "스택 영역",
  normalizedArea: "정규화된 영역",
  columnChart: "열 차트",

  barChart: "막대 차트",

  pieChart: "원형 차트",

  lineChart: "선형 차트",
  xyChart: "X Y (산점도)",

  areaChart: "영역 차트",

  histogramChart: "히스토그램",
  rangeBarChart: "범위 막대",
  rangeAreaChart: "범위 영역",
  combinationChart: "조합 차트",

  polarChart: "극좌표 차트",
  radarLine: "방사형 선형 차트",
  radarArea: "방사형 영역 차트",
  nightingale: "나이팅게일 차트",
  radialColumn: "방사형 열 차트",
  radialBar: "방사형 막대 차트",

  statisticalChart: "통계 차트",
  boxPlot: "상자 그림",
  histogram: "히스토그램",
  rangeBar: "범위 막대",
  rangeArea: "범위 영역",

  hierarchicalChart: "계층형 차트",
  organizationChart: "조직도 차트",
  treeChart: "트리 차트",

  specializedChart: "특수 차트",
  heatMap: "히트맵",
  treemap: "트리맵",
  waterfall: "폭포 차트",

  // 조합 차트
  columnLineCombo: "열 & 선형 차트", // Column & Line
  areaColumnCombo: "영역 & 열 차트", // Area & Column

  // 계층형 차트
  sunburst: "선버스트", // Sunburst

  // 특수 차트
  heatmap: "히트맵",
};

//#endregion

//#region SnGrid Props

interface SnTreeProps {
  menuInfo?: menuInfo; // 메뉴코드 추가
  columnDefs?: ColDef[]; // 직접 전달된 컬럼 정의
  columnConfig?: ColumnConfig[]; // 컬럼 설정을 기반으로 생성할 경우
  rowData: RowData[] | null; // null 허용
  keyField?: string[]; // 고유 키로 사용할 필드들 (기본값: ["id"])
  gridOptions?: any; // AG Grid 옵션
  style?: React.CSSProperties; // 스타일 (선택적)
  onGridReady?: (params: any) => void; // 그리드 초기화 후 호출할 함수
  enableExternalChart?: boolean; // 독립 차트 활성화 여부
  chartColumns?: string[]; // 차트로 사용할 컬럼 리스트
  isEditable?: boolean; // 수정 가능 여부
  enableAddRow?: boolean; // 행 추가 활성화 여부
  rowSelection?: "single" | "multiple"; // 행 선택 모드
  pagination?: boolean; // Paging 여부
  paginationPageSize?: number; // Paging Size
  paginationPageSizeSelector?: any[];
  enableRangeSelection?: boolean; // 블록 지정 활성화
  rowGroupPanelShow?: "onlyWhenGrouping" | "never" | "always";
  animateRows?: boolean; // 행 애니메이션 활성화
  rowDragManaged?: boolean; // 행 드래그 활성화
  suppressMoveWhenRowDragging?: boolean; // 행 드래그 이동 억제?
  cellSelection?: boolean; // Cell 선택
  suppressNoRowsOverlay?: boolean; // 그리드에 no rows to show 제거
  suppressMenuHide?: boolean; // Column Menu visilble
  ischeckboxSelection?: boolean; // 체크박스 셀렉션
  onRowClicked?: (event: any) => void; // row 클릭
  onCellValueChanged?: (event: any) => void; // 셀 값 변경 이벤트
  onCellDoubleClicked?: (event: any) => void; // 셀 dblclick  이벤트
  onBeforeAddRow?: (newRow: RowData) => boolean; // 행 추가 전 이벤트
  onBeforeDeleteRows?: (rowsToDelete: RowData[]) => boolean; // 행 삭제 전 이벤트
  onBeforeEdit?: (rowData: RowData, colDef: any) => boolean; // 셀 수정 전 이벤트
  onFocusedRowChanged?: (focusedRowData: RowData | null) => void;
  onRowDoubleClicked?: (e: any) => void;
  onSelectionChanged?: (selectedRows: any[]) => void; //  추가
  seq?: number; // 그리드 순번
  title?: string;
  onBtnAddClick?: (id: string) => void;
  onBtnDeleteClick?: (id: string) => void;
  header?: React.ReactNode;
  onContextMenu?: (event: any) => void;
  // 여기서부터 트리리
  loading?: boolean;
  treeData?: boolean;
  headerHeight?: number;
  autoGroupColumnDef?: any;
  groupDefaultExpanded?: number;
  getDataPath?: (rowData: { path_arr: any[] }) => any[];
}

//#endregion

const SnTree = forwardRef((props: SnTreeProps, ref) => {
  // 구조 분해를 통해 props에서 필요한 속성 추출
  const {
    columnDefs, // 직접 전달된 columnDefs
    columnConfig, // columnConfig를 기반으로 columnDefs 생성
    menuInfo,
    rowData: initialRowData,
    keyField = ["KeyField"], // 기본적으로 "KeyField"를 고유 식별자로 사용
    gridOptions = {},
    style,
    onGridReady,
    enableExternalChart = false, // 독립 차트 기본값: 비활성화
    chartColumns = [], // 차트로 사용할 기본 컬럼 (없음)
    isEditable = false,
    enableAddRow = false,
    // rowSelection = "single", // 기본값: single
    rowSelection: rowSelectionMode,
    pagination = false,
    paginationPageSize = 50,
    paginationPageSizeSelector = [50, 100],
    enableRangeSelection = true,
    rowGroupPanelShow = "onlyWhenGrouping",
    animateRows = true,
    rowDragManaged = false,
    suppressMoveWhenRowDragging = false,
    cellSelection = false, // 포커스 관련으로 FALSE처리함
    suppressNoRowsOverlay = true,
    suppressMenuHide = false,
    ischeckboxSelection = false, // 체크박스 셀렉션
    onRowClicked, // 로우 클릭
    onCellValueChanged, // 값 수정
    onCellDoubleClicked, // 셀더블클릭
    onBeforeAddRow, // 행 추가 전 이벤트
    onBeforeDeleteRows, // 행 삭제 전 이벤트
    onBeforeEdit, // 수정 전 이벤트
    onFocusedRowChanged, // 새로운 prop
    onRowDoubleClicked,
    onSelectionChanged,
    title,
    onBtnAddClick,
    onBtnDeleteClick,
    header,
    onContextMenu,
    // 트리 사용으로 추가
    loading = false,
    treeData = true,
    getDataPath,
    groupDefaultExpanded,
    autoGroupColumnDef,
    headerHeight,
  } = props;

  //#region KeyField 고유 키 생성 함수
  const usedKeys = new Set<string>(); // 이미 사용된 키를 추적
  const [errorMap, setErrorMap] = useState(new Map()); // 에러메시지
  const { menuItems } = useMenuStore();
  // 전역 변수 선언

  let cdGrid: string | null = null;

  const generateKey = () => {
    let key;
    do {
      key = uuidv4(); // 항상 새로운 UUID 생성
    } while (usedKeys.has(key)); // 중복 여부 확인
    usedKeys.add(key);
    return key;
  };

  //#endregion
  const { userInfo } = global();
  // State로 columnDefs 관리
  const [resolvedColumnDefs, setResolvedColumnDefs] = useState<ColDef[]>([]);

  // editable 여부를 제어하는 함수
  const isEditableFunc = (field: string): boolean => {
    return isEditable && field !== "readonly_field";
  };

  // 이전 포커스된 행 데이터 저장용 Ref
  const previousFocusedRowRef = useRef<RowData | null>(null);

  // columnConfig 변경 시에만 createColumnDefs 실행
  const memoizedColumnConfig = useMemo(() => columnConfig, [columnConfig]);
  const [requireds, setRequireds] = useState<any>({}); /* */
  const [multipopups, setMultipopup] = useState<any>({}); /* */
  const [singlepopups, setSinglepopup] = useState<any>({}); /* */
  const [editCons, setEditCons] = useState<any>({}); /* */
  useEffect(() => {
    const loadColumnDefs = async () => {
      // console.log("==================loadColumnDefs.");
      let updatedColumnDefs:
        | any[]
        | ((prevState: ColDef<any, any>[]) => ColDef<any, any>[]) = [];

      if (columnDefs) {
        // columnDefs가 직접 전달된 경우 그대로 설정
        updatedColumnDefs = columnDefs;
      } else if (memoizedColumnConfig) {
        // columnConfig가 변경된 경우에만 비동기 함수 호출

        // memoizedColumnConfig의 첫 번째 항목에서 cd_grid 추출
        cdGrid = memoizedColumnConfig[0]?.cd_grid || null;

        // 2025.01.08 필수값 체크용
        const requiredsFilter = memoizedColumnConfig.filter(
          (col) => col.yn_required == "Y"
        );
        let tempreq = Object.assign({});
        requiredsFilter.map((req) => {
          tempreq[req.cd_column] = req.nm_column;
        });
        setRequireds(tempreq);

        const editConsFilter = memoizedColumnConfig.filter(
          (col) => col.dc_editcon != "" && col.dc_editcon != null
        );
        let tempeditCons = Object.assign({});
        editConsFilter.map((con) => {
          tempeditCons[con.cd_column] = con.dc_editcon;
        });
        setEditCons(tempeditCons);

        // 2025.01.14 팝업용
        const multiSetting = memoizedColumnConfig.filter(
          (col) => col.tp_column == "SnMultiCodeBox"
        );
        setMultipopup(multiSetting);
        const singleSetting = memoizedColumnConfig.filter(
          (col) => col.tp_column == "SnCodeBox"
        );
        setSinglepopup(singleSetting);

        const generatedColumnDefs = await createColumnDefs(
          memoizedColumnConfig,
          isEditableFunc,
          userInfo,
          errorMap
          // ,          gridRefcol
        );
        // updatedColumnDefs = generatedColumnDefs;
        updatedColumnDefs = generatedColumnDefs.map((colDef) => ({
          ...colDef,
          mainMenuItems: mainMenuItems, // 공통 커스텀 메뉴 추가
        }));
      }

      // checkboxSelection이 필요한 경우 컬럼 추가

      if (ischeckboxSelection) {
        updatedColumnDefs = [
          {
            headerName: "",
            checkboxSelection: true,
            headerCheckboxSelection: true,
            suppressColumnsToolPanel: true,
            headerCheckboxSelectionFilteredOnly: true, //  현재 필터링된 행만 체크/언체크
            width: 50,
            colId: "fixCheckbox",
          },
          ...updatedColumnDefs,
        ];
      }
      setResolvedColumnDefs(updatedColumnDefs);
    };

    loadColumnDefs();
  }, [columnDefs, memoizedColumnConfig]);

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: true, // 헤더 필터 비활성화
      floatingFilter: false, // 플로팅 필터 비활성화
      resizable: true,
      suppressMenu: true,
      enableRowGroup: true,
      editable: isEditable, // 수정 가능 여부를 props로 제어
      headerClass: "header-center",
    }),
    []
  );

  const columnSave = async () => {
    try {
      if (!memoizedColumnConfig || memoizedColumnConfig.length === 0) return;
      const columnState = gridRef.current?.api
        .getColumnState()
        .filter((colDef: any) => !colDef.colId?.startsWith("fix"));
      if (!columnState || columnState.length === 0) return;

      const updatedColumnDefs = memoizedColumnConfig.map((colDef: any) => {
        // columnState에서 동일한 colId 찾기
        const matchingColumn = columnState?.find(
          (state) => state.colId === colDef.cd_column
        );
        const matchingIndex = columnState?.findIndex(
          (state) => state.colId === colDef.cd_column
        );

        return {
          ...colDef,
          cd_user: userInfo.cd_user,
          qt_width: matchingColumn?.width ?? colDef.qt_width,
          no_seq: matchingIndex !== -1 ? matchingIndex : colDef.no_seq,
          fg_visible:
            matchingColumn?.hide !== undefined
              ? matchingColumn.hide
                ? "N"
                : "Y"
              : colDef.fg_visible,
        };
      });

      if (memoizedColumnConfig[0].cd_user !== userInfo.cd_user) {
        modifiedRows.current.added.push(...updatedColumnDefs);
      } else {
        modifiedRows.current.updated.push(...updatedColumnDefs);
      }
      const changesRow = getChange();
      const SaveData = {
        sys_formgridcolumn: changesRow,
      };
      const response = await fnSave("grid", SaveData);
      resetUpdate();
    } catch (error: any) {}
  };

  const columnInit = async () => {
    try {
      if (!memoizedColumnConfig || memoizedColumnConfig.length === 0) return;

      if (memoizedColumnConfig[0].cd_user === userInfo.cd_user) {
        modifiedRows.current.deleted.push(...memoizedColumnConfig);
      }
      const changesRow = getChange();
      const SaveData = {
        sys_formgridcolumn: changesRow,
      };
      const response = await fnSave("grid", SaveData);
      resetUpdate();
    } catch (error: any) {}
  };

  const filterBoxRef = useRef<{ openFilterBox: () => void } | null>(null);

  const mainMenuItems = (
    params: GetMainMenuItemsParams
  ): (MenuItemDef | string)[] => {
    const athleteMenuItems: (MenuItemDef | string)[] = [
      {
        name: "항목설정저장",
        action: () => {
          columnSave();
        },
      },
      {
        name: "항목 제거",
        action: () => {
          //const columnId = params.column.getColId();
          //gridRef.current?.api.setColumnVisible(columnId, false);
        },
      },
    ];
    //항목복원은 ag grid 기본기능(columnChooser)
    const columnChooseItem = params.defaultItems.find(
      (item) => item === "columnChooser"
    );
    if (columnChooseItem) {
      athleteMenuItems.push(columnChooseItem);
    }
    athleteMenuItems.push(
      {
        name: "항목 초기화",
        action: () => {
          columnInit();
        },
      },

      ...params.defaultItems.filter(
        (_, index) => ![0, 1, 9, 10, 11].includes(index)
      )
    );
    athleteMenuItems.push(
      "separator",
      {
        name: "필터/ 필터해제",
        action: () => {
          const headerIcons = document.querySelectorAll(
            ".ag-header-cell .ag-header-icon"
          );

          headerIcons.forEach((icon) => {
            icon.classList.toggle("hidden-filter"); // 필터 아이콘을 숨기거나 보이게 토글
          });
        },
      },
      {
        name: "찾기",
        action: () => {
          filterBoxRef.current?.openFilterBox();
        },
      },
      {
        name: "순서변경",
        action: () => {
          setTimeout(() => {
            enableRowDrag();
          }, 100);
        },
      }
    );
    return athleteMenuItems;
  };

  const commonAutoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      minWidth: 200,
      suppressMenu: false,
      cellRendererParams: {
        innerRenderer: (params: any) => {
          return params.value;
        },
      },
      mainMenuItems: (
        params: GetMainMenuItemsParams
      ): (MenuItemDef | DefaultMenuItem)[] => {
        const customMenuItems: MenuItemDef[] = [
          {
            name: "모든 그룹 해제",
            action: () => {
              params.api.setRowGroupColumns([]); // 모든 그룹 해제
            },
          },
          {
            name: "전체 확장",
            action: () => {
              params.api.expandAll(); // 전체 확장
            },
          },
          {
            name: "전체 축소",
            action: () => {
              params.api.collapseAll(); // 전체 축소
            },
          },
        ];

        // ✅ 필요하다면 기본 메뉴 항목도 추가 가능
        // return ['pinSubMenu', 'separator', ...customMenuItems];
        return customMenuItems;
      },
    };
  }, []);

  const mergedAutoGroupColumnDef: ColDef = {
    ...commonAutoGroupColumnDef,
    ...autoGroupColumnDef,
    cellRendererParams: {
      ...commonAutoGroupColumnDef.cellRendererParams,
      ...autoGroupColumnDef?.cellRendererParams,
    },
  };

  const enableRowDrag = () => {
    if (!gridRef.current) return;

    let currentColumnDefs = gridRef.current?.api.getColumnDefs();

    if (currentColumnDefs) {
      const currentFilterModel = gridRef.current.api.getFilterModel();

      currentColumnDefs = currentColumnDefs.filter(
        (col: any) => col.colId !== "fixIndicator"
      );
      const isRowDragColumnExists = currentColumnDefs.some(
        (col: any) => col.colId === "fixDrag"
      );

      if (isRowDragColumnExists) {
        currentColumnDefs = currentColumnDefs.filter(
          (col: any) => col.colId !== "fixDrag"
        );
        setResolvedColumnDefs(currentColumnDefs); // 상태 업데이트
      } else {
        const updatedColumnDefs = [
          {
            headerName: "순서 변경",
            field: "rowDrag",
            rowDrag: true,
            editable: false,
            width: 50,
            colId: "fixDrag",
            sortable: false,
            filter: false,
            suppressMovable: true,
          },
          ...currentColumnDefs,
        ];

        setResolvedColumnDefs(updatedColumnDefs); // 상태 업데이트
      }
    }
  };

  //#endregion

  //#region 초기화

  const gridRef = useRef<AgGridReact>(null); // AG Grid 내부 참조

  const [isGridReady, setIsGridReady] = useState(false);

  // Generate a unique storage key based on URL and component ID
  const getStorageKey = () => {
    // debugger;
    const baseKey = menuInfo?.cd_menu; // Current page path

    const componentId = cdGrid || "defaultGrid"; // gridId가 없으면 기본값 사용
    return `gridColumnState_${baseKey}_${componentId}`;
  };

  // Save column state to local storage
  const saveColumnState = () => {
    //debugger;
    if (gridRef.current?.api) {
      const columnState = gridRef.current.api.getColumnState();
      const key = getStorageKey();

      const previousState = localStorage.getItem(key);

      if (
        previousState &&
        JSON.stringify(JSON.parse(previousState)) ===
          JSON.stringify(columnState)
      ) {
        console.log("Column state unchanged. Skipping save.");
        return; // 저장하지 않음
      }

      localStorage.setItem(key, JSON.stringify(columnState));

      console.log(`Column state save :`, columnState);
    }
  };

  // Restore column state from local storage
  const restoreColumnState = () => {
    console.log(`******restoreColumnState`);
    if (gridRef.current?.api) {
      const columnState = localStorage.getItem(getStorageKey());
      if (columnState && gridRef.current?.api) {
        const parsedState = JSON.parse(columnState);

        gridRef.current.api.applyColumnState({
          state: parsedState,
          applyOrder: true,
        });
        console.log(`Column state restored:`, parsedState);
      } else {
        console.log(
          `No column state found for key: ${getStorageKey()}. Using defaults.`
        );
      }
    }
  };

  // Debounce 적용
  let debounceTimeout: number;

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    return (...args: any[]) => {
      clearTimeout(debounceTimeout); // timeout을 해제
      debounceTimeout = window.setTimeout(() => func(...args), delay); // setTimeout 사용
    };
  };

  const handleColumnStateChange = debounce(() => {
    if (gridRef.current?.api) {
      // 컬럼 상태 가져오기
      const columnState = gridRef.current.api.getColumnState();
      console.log("handleColumnStateChange:", columnState);

      // resolvedColumnDefs 업데이트
      const updatedColumnDefs: CustomColDef[] = columnState
        .map((state) => {
          const matchingColDef = resolvedColumnDefs.find(
            (colDef) => colDef.field === state.colId
          );

          if (!matchingColDef) return null;

          return {
            ...matchingColDef,
            hide: state.hide ?? false, // hide 처리
            pinned: state.pinned ?? null, // 고정 상태
            width: state.width ?? matchingColDef.width, // 기존 너비 유지
          } as CustomColDef; // 명시적으로 ColDef 단언
        })
        .filter((colDef): colDef is CustomColDef => colDef !== null); // null 제거 및 타입 단언

      setResolvedColumnDefs(updatedColumnDefs);
      console.log("Updated Column Definitions:", updatedColumnDefs);
    }
  }, 300);

  // 데이터 초기화

  const [rowData, setRowData] = useState<RowData[]>(
    (initialRowData || []).map((row) => {
      const key = row.KeyField || generateKey();
      return { ...row, KeyField: key };
    })
  );

  useEffect(() => {
    if (isGridReady && initialRowData) {
      gridRef.current?.api.showLoadingOverlay(); // 로딩 오
      setRowData(
        initialRowData.map((row) => ({
          ...row,
          KeyField: row.KeyField || generateKey(),
        }))
      );
      // restoreColumnState(); // 컬럼 상태 복원
      gridRef.current?.api.hideOverlay();
    }
  }, [isGridReady, initialRowData]);

  // 변경된 행 상태 추적
  const modifiedRows = useRef({
    added: [] as RowData[],
    updated: [] as RowData[],
    deleted: [] as RowData[],
  });
  //#endregion

  //#region Footer 관련 내용
  // 하단 고정 행 데이터를 관리하는 상태
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState<any[]>([]);

  //#region Footer 합계 계산

  const formatNumber = (value: number) =>
    value ? Number(value).toLocaleString() : "0";

  const computeFooterSummary = () => {
    // debugger;
    if (!gridRef.current?.api) return;

    const renderedNodes = gridRef.current.api.getRenderedNodes();
    const renderedData = renderedNodes.map((node) => node.data);

    // resolvedColumnDefs를 CustomColDef로 타입 단언
    const customColumnDefs = resolvedColumnDefs as CustomColDef[];

    // yn_sum === "Y"인 컬럼 필터링
    const targetFields = customColumnDefs
      .filter((col) => col.yn_sum === "Y") // yn_sum이 "Y"인 컬럼만
      .map((col) => col.field) // 필드 이름만 추출
      .filter((field): field is string => !!field); // 유효한 필드만 유지

    const totalRow = targetFields.reduce(
      (acc, field) => {
        const sum = renderedData.reduce(
          (sum, row) => sum + (row[field] || 0),
          0
        );
        acc[field] = sum;
        return acc;
      },
      {} as Record<string, number>
    );

    if (JSON.stringify(pinnedBottomRowData[0]) !== JSON.stringify(totalRow)) {
      setPinnedBottomRowData([totalRow]);
    }
  };

  // //#endregion

  // 초기화 및 데이터 변경 시 합계 계산
  useEffect(() => {
    //    console.log("==================useEffect calculateFooter.");
    computeFooterSummary();
  }, [rowData]);

  const getFooterDataAsync = () => {
    return new Promise((resolve) => {
      computeFooterSummary();
      setTimeout(() => resolve(pinnedBottomRowData[0]), 0); // 상태 업데이트 대기
    });
  };

  // 특정 필드의 푸터 값 가져오기
  const getFooterData = () => {
    const footer = pinnedBottomRowData.length > 0 ? pinnedBottomRowData[0] : {};
    return new Proxy(footer, {
      get: (target, prop: string) => (prop in target ? target[prop] : null),
    });
  };

  //#region 1. 행 추가

  const getFirstEditableColumn = (columnDefs: ColDef[], defaultColDef: any) => {
    return columnDefs.find((col) => {
      if (col.editable === true) return true; // 명시적으로 editable이 true인 경우
      if (col.editable === false) return false; // editable이 false면 제외
      if (typeof col.editable === "function") {
        // EditableCallbackParams 타입의 더미 데이터를 제공
        const params: EditableCallbackParams = {
          node: {} as any, // 필요에 따라 노드 객체를 설정하거나 빈 객체로 처리
          data: {}, // row data
          column: {} as any, // 현재 컬럼 정보
          colDef: col, // 현재 컬럼 정의
          api: {} as any, // ag-Grid API 객체
          context: {}, // 그리드의 context 정보
        };

        // editable 함수 실행 결과 확인
        return col.editable(params);
      }
      return defaultColDef?.editable === true; // editable이 undefined면 defaultColDef를 참조
    })?.field;
  };

  const getSelectedRow = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    // return selectedRows && selectedRows.length > 0 ? selectedRows[0] : null;
    return selectedRows && selectedRows.length > 0 ? selectedRows : null;
  };

  const getSelectedRows = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    return selectedRows && selectedRows.length > 0 ? selectedRows : null;
  };

  const addRow = (newRow: RowData = {}) => {
    if (onBeforeAddRow && onBeforeAddRow(newRow) === false) {
      return;
    }
    saveExpandState();
    gridRef.current?.api.showLoadingOverlay();
    const newKey = generateKey();

    const newData = { ...newRow, KeyField: newKey, state: "insert" };
    // 1. rowData 업데이트

    modifiedRows.current.added.push(newData); // 추가된 행 기록

    gridRef.current?.api.applyTransaction({ add: [newData] });

    setRowData((prev) => [...prev, newData]); // 데이터 추가
    setTimeout(() => {
      const api = gridRef.current?.api;
      if (!api) {
        return;
      }

      restoreExpandState();

      setTimeout(() => {
        const focuscell = gridRef.current?.api.getFocusedCell();
        if (focuscell) {
          const rowIndex = focuscell.rowIndex; // 포커스된 셀의 인덱스
          const rowNode = api.getDisplayedRowAtIndex(rowIndex);
          if (rowNode) {
            const childNodes = rowNode.childrenAfterSort; // 정렬된 자식들

            if (childNodes && childNodes.length > 0) {
              const lastChildNode = childNodes[childNodes.length - 1]; // 마지막 자식 노드
              const newRowIndex: any = lastChildNode.rowIndex;

              if (
                resolvedColumnDefs === undefined ||
                resolvedColumnDefs.length === 0
              ) {
                if (newRowIndex !== undefined) {
                  api.ensureIndexVisible(newRowIndex);
                  api.clearFocusedCell();
                  api.setFocusedCell(newRowIndex, "nm_menu");
                  api.ensureIndexVisible(newRowIndex);
                  api.getDisplayedRowAtIndex(newRowIndex)?.setSelected(true);

                  const focusedEvent: CellFocusedEvent = {
                    type: "cellFocused",
                    api: api,
                    column: api.getColumn("nm_menu")!,
                    rowIndex: newRowIndex,
                    context: undefined,
                    rowPinned: undefined,
                  };
                  handleCellFocused(focusedEvent);
                }
              } else {
                const firstEditableCol =
                  resolvedColumnDefs.find((col) => col.editable)?.field ||
                  resolvedColumnDefs[0]?.field;

                if (newRowIndex !== undefined && firstEditableCol) {
                  api.ensureIndexVisible(newRowIndex);
                  api.setFocusedCell(newRowIndex, firstEditableCol);
                  api.getDisplayedRowAtIndex(newRowIndex)?.setSelected(true);

                  const focusedEvent: CellFocusedEvent = {
                    type: "cellFocused",
                    api: api,
                    column: api.getColumn(firstEditableCol)!,
                    rowIndex: newRowIndex,
                    context: undefined,
                    rowPinned: undefined,
                  };
                  handleCellFocused(focusedEvent);
                }
              }
            }
          }
        }
        gridRef.current?.api.hideOverlay();
      }, 100);
    }, 50);
  };

  //#endregion

  //#region  2. 선택된 행 삭제

  const handleRowFocusAfterDelete = (
    deletedRowIndex: number,
    gridApi: GridApi
  ) => {
    const remainingRows = gridApi.getDisplayedRowCount();

    if (remainingRows === 0) {
      console.warn("남아 있는 행이 없습니다.");
      return;
    }

    // 삭제된 행의 이전 행으로 이동
    let rowIndexToFocus = deletedRowIndex - 1;
    if (rowIndexToFocus < 0) {
      rowIndexToFocus = 0; // 첫 번째 행으로 이동
    }

    const rowNode = gridApi.getDisplayedRowAtIndex(rowIndexToFocus);
    if (!rowNode) {
      rowIndexToFocus = deletedRowIndex; // 다음 행으로 이동
    }

    const focusedRowNode = gridApi.getDisplayedRowAtIndex(rowIndexToFocus);
    if (focusedRowNode) {
      focusedRowNode.setSelected(true); // 행 선택
      gridApi.ensureIndexVisible(rowIndexToFocus); // 행 가시화

      // 첫 번째 수정 가능한 컬럼에 포커스 설정
      const firstEditableCol = resolvedColumnDefs.find(
        (col) => col.editable
      )?.field;
      if (firstEditableCol) {
        gridApi.setFocusedCell(rowIndexToFocus, firstEditableCol);
      }
    }
  };

  const deleteSelectedRows = () => {
    const selectedNodes = gridRef.current?.api.getSelectedNodes();

    if (!selectedNodes || selectedNodes.length === 0) {
      console.warn("삭제할 행을 선택하세요.");
      return;
    }

    const selectedKeys = selectedNodes.map((node) => node.data.KeyField);

    const rowsToDelete = rowData.filter((row) =>
      selectedKeys.includes(row.KeyField)
    );

    // 삭제된 행의 첫 번째 노드의 인덱스
    const firstSelectedIndex = selectedNodes[0]?.rowIndex || 0;

    const rowsToRemove = selectedNodes.map((node) => node.data); // 데이터만 추출
    gridRef.current?.api.applyTransaction({ remove: rowsToRemove }); // 삭제 요청

    saveExpandState();

    // 상태 업데이트
    setRowData((prev) =>
      prev.filter((row) => !selectedKeys.includes(row.KeyField))
    );

    // 변경된 행 관리
    rowsToDelete.forEach((row) => {
      if (
        !modifiedRows.current.added.find((r) => r.KeyField === row.KeyField)
      ) {
        modifiedRows.current.deleted.push(row);
      }
    });

    modifiedRows.current.added = modifiedRows.current.added.filter(
      (row) => !selectedKeys.includes(row.KeyField)
    );

    gridRef.current?.api.clearFocusedCell();
    gridRef.current?.api.deselectAll();

    setTimeout(() => {
      if (gridRef.current?.api) {
        //handleRowFocusAfterDelete(firstSelectedIndex, gridRef.current.api);

        restoreExpandState();
      } else {
        console.warn("Grid API null error.");
      }
    }, 0);
  };

  const deleteRowByIndex = (rowIndex: number) => {
    if (!gridRef.current?.api) {
      console.warn("Grid API is not available.");
      return;
    }

    const node = gridRef.current.api.getDisplayedRowAtIndex(rowIndex);

    if (!node) {
      console.warn(`Row at index ${rowIndex} not found.`);
      return;
    }

    const rowDataToDelete = node.data;

    gridRef.current.api.applyTransaction({ remove: [rowDataToDelete] });

    // 상태 업데이트: 삭제된 행 제거
    setRowData((prev) =>
      prev.filter((row) => row.KeyField !== rowDataToDelete.KeyField)
    );

    // 변경된 행 관리
    if (
      !modifiedRows.current.added.find(
        (addedRow) => addedRow.KeyField === rowDataToDelete.KeyField
      )
    ) {
      modifiedRows.current.deleted.push(rowDataToDelete);
    }

    //computeFooterSummary();

    // `added`에서 삭제된 행 제거
    modifiedRows.current.added = modifiedRows.current.added.filter(
      (row) => row.KeyField !== rowDataToDelete.KeyField
    );
    console.log("Deleted row:", rowDataToDelete);

    // 삭제 후 포커스 조정
    setTimeout(() => {
      if (gridRef.current?.api) {
        handleRowFocusAfterDelete(rowIndex, gridRef.current.api);
      } else {
        console.warn("api null error.");
      }
    }, 0);
  };

  const deleteAllRows = () => {
    if (!gridRef.current?.api) {
      console.warn("Grid API is not available.");
      return;
    }
    console.warn("현재 렌더링된 노드 가져오기.");

    // 현재 렌더링된 노드 가져오기
    const renderedNodes = gridRef.current.api.getRenderedNodes();

    if (!renderedNodes || renderedNodes.length === 0) {
      return;
    }

    const visibleKeys = renderedNodes.map((node) => node.data.KeyField);

    const rowsToDelete = rowData.filter((row) =>
      visibleKeys.includes(row.KeyField)
    );

    const rowsToRemove = renderedNodes.map((node) => node.data); // 데이터만 추출
    gridRef.current?.api.applyTransaction({ remove: rowsToRemove }); // 삭제 요청

    // 상태 업데이트: 삭제된 행 제거
    setRowData((prev) =>
      prev.filter((row) => !visibleKeys.includes(row.KeyField))
    );

    // 변경된 행 관리
    rowsToDelete.forEach((row) => {
      if (
        !modifiedRows.current.added.find(
          (addedRow) => addedRow.KeyField === row.KeyField
        )
      ) {
        modifiedRows.current.deleted.push(row);
      }
    });

    // // Footer 재계산 추가

    //computeFooterSummary();

    // `added`에서 삭제된 행 제거
    modifiedRows.current.added = modifiedRows.current.added.filter(
      (row) => !visibleKeys.includes(row.KeyField)
    );

    console.log("Deleted visible rows:", rowsToDelete);
  };

  //#endregion

  // 포커스된 행 변경 처리
  const handleCellFocused = (event: CellFocusedEvent) => {
    const focusedRowIndex = event.rowIndex;
    let focusedRowData: any = null;

    if (focusedRowIndex === null) {
      return;
    }

    if (focusedRowIndex != null) {
      focusedRowData =
        gridRef.current?.api.getDisplayedRowAtIndex(focusedRowIndex)?.data ||
        null;
    }

    if (
      typeof rowSelection === "object" &&
      rowSelection !== null &&
      rowSelection.mode === "singleRow"
    ) {
      const selectedRows = gridRef.current?.api.getSelectedRows() || [];
      const selectedRowData = selectedRows.length > 0 ? selectedRows[0] : null;
      gridRef.current?.api.clearCellSelection(); // 기존 셀 초기화인데

      if (focusedRowData != selectedRowData) {
        gridRef.current?.api.deselectAll(); // 기존 선택 초기화

        const focusedRowNode =
          gridRef.current?.api.getDisplayedRowAtIndex(focusedRowIndex);
        focusedRowNode?.setSelected(true); // 새 행 선택
      }
    }

    if (
      typeof rowSelection === "object" &&
      rowSelection !== null &&
      rowSelection.mode === "multiRow"
    ) {
      const selectedRows = gridRef.current?.api.getSelectedRows() || [];
      const cellRanges = gridRef.current?.api.getCellRanges() || [];
      const focusedCell = gridRef.current?.api.getFocusedCell();

      // 여러 개 선택된 경우에는 로직 실행하지 않음
      if (cellRanges.length !== 1) return;

      if (focusedCell && cellRanges.length > 0) {
        const selectedRange = cellRanges[0]; // 하나만 선택됐다고 가정
        const selectedCell = {
          rowIndex: selectedRange.startRow?.rowIndex,
          column: selectedRange.columns[0]?.getColId(),
        };

        if (
          selectedCell.rowIndex !== focusedCell.rowIndex ||
          selectedCell.column !== focusedCell.column.getColId()
        ) {
          gridRef.current?.api.clearCellSelection();
        }
      }

      const selectedRowData = selectedRows[0];

      if (focusedRowData !== selectedRowData) {
        gridRef.current?.api.deselectAll(); // 기존 선택 초기화

        const focusedRowNode =
          gridRef.current?.api.getDisplayedRowAtIndex(focusedRowIndex);
        focusedRowNode?.setSelected(true); // 새 행 선택
      }
    }

    // 포커스된 행이 변경된 경우만 처리
    if (
      focusedRowData !== previousFocusedRowRef.current &&
      onFocusedRowChanged
    ) {
      onFocusedRowChanged(focusedRowData); // 외부로 이벤트 알림
      previousFocusedRowRef.current = focusedRowData; // 상태 업데이트
    }

    let colId: string | undefined;
    if (typeof event.column !== "string") {
      colId = event.column?.getColId(); // 안전한 접근
    } else {
      colId = event.column; // 이미 string 타입인 경우 그대로 사용
    }

    if (editCons != null && editCons[colId ? colId : ""] != null) {
      let condata: string = editCons[colId ? colId : ""];

      if (
        Object.hasOwn(focusedRowData, "state") &&
        focusedRowData.state === "insert"
      )
        condata = condata.replace("new", "false");
      else condata = condata.replace("new", "true");

      Object.keys(focusedRowData).forEach((key) => {
        condata = condata.replace(key, focusedRowData[key]);
      });
      const matchingColDef = resolvedColumnDefs.find(
        (colDef) => colDef.field === colId
      );
      const updatedColumnDefs = {
        ...matchingColDef,
        editable: new Function(`return !(${condata});`)(),
      } as CustomColDef; // 명시적으로 ColDef 단언

      setResolvedColumnDefs((prevDefs) => {
        return prevDefs.map((def) =>
          def.field === colId ? { ...def, ...updatedColumnDefs } : def
        );
      });
    }
  };

  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    if (rowSelectionMode === "single") {
      return {
        mode: "singleRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      };
    } else if (rowSelectionMode === "multiple") {
      return {
        mode: "multiRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      };
    } else {
      return "single"; // 기본값
    }
  }, [rowSelectionMode]);

  function shouldSkipCellAction(event: CellKeyDownEvent): boolean {
    const focusedRowIndex = event.rowIndex;
    if (focusedRowIndex === null) return true;

    let colId: string | undefined;

    if (typeof event.column !== "string") {
      colId = event.column?.getColId();
    } else {
      colId = event.column;
    }

    let focusedRowData: any =
      gridRef.current?.api.getDisplayedRowAtIndex(focusedRowIndex)?.data ??
      null;

    if (!focusedRowData) return true;

    let condata: string = editCons[colId ?? ""];
    if (!condata) return false;
    if (condata === "true") return false;
    //state 처리
    condata = condata.replace(
      "new",
      focusedRowData.state === "insert" ? "false" : "true"
    );
    // 데이터 키워드 치환
    Object.keys(focusedRowData).forEach((key) => {
      const value = focusedRowData[key];
      condata = condata.replace(new RegExp(`\\b${key}\\b`, "g"), value);
    });

    try {
      return !!new Function(`return (${condata});`)();
    } catch (error) {
      console.error("조건 파싱 에러:", condata, error);
      return false;
    }
  }

  let isProcessing = false;
  // 키보드 입력 처리
  const handleCellKeyDown = async (event: CellKeyDownEvent) => {
    const gridEvent = event.event;

    if (!gridEvent || !(gridEvent instanceof KeyboardEvent)) return;

    const { api, node, column } = event;
    const key = gridEvent.key;

    if (!node || !column || node.rowIndex === null) {
      return;
    }

    console.log("KeyDown Event Triggered:", key);

    // 숫자 및 달력 필드에서 방향키 동작 방지
    const columnDef = column.getColDef() as CustomColDef; // 확장된 타입 사용

    const isNumberOrDateField =
      columnDef?.type === "SnNumberBox" || columnDef?.type === "SnDateBox";

    if (isNumberOrDateField && (key === "ArrowUp" || key === "ArrowDown")) {
      gridEvent.preventDefault(); // 기본 동작(숫자/날짜 변경) 차단
    }

    // 방향키 처리
    if (key === "ArrowUp" || key === "ArrowDown") {
      const isEditing = api.getEditingCells().length > 0; // 현재 에디트 모드인지 확인

      if (isEditing) {
        console.log(`Exiting edit mode for key: ${key}`);
        api.stopEditing(); // 에디트 모드 종료

        // 방향키에 따른 포커스 이동
        const targetRowIndex =
          key === "ArrowUp" ? node.rowIndex - 1 : node.rowIndex + 1;

        // 유효한 행인지 확인
        if (
          targetRowIndex >= 0 &&
          targetRowIndex < api.getDisplayedRowCount()
        ) {
          api.setFocusedCell(targetRowIndex, column.getColId()); // 동일한 컬럼에 포커스
          api.ensureIndexVisible(targetRowIndex); // 포커스된 행 가시화
        }
      }

      return; // 방향키 처리는 여기서 종료
    }

    // 기존 방향키 로직 유지
    if (key === "ArrowLeft") {
      const columns = api.getColumns?.(); // getColumns 호출
      if (!columns || columns.length === 0) return; // null 및 빈 배열 처리

      const currentColumnIndex = columns.findIndex(
        (col) => col.getColId() === column.getColId()
      );

      if (currentColumnIndex > 0) {
        const previousColumn = columns[currentColumnIndex - 1];
        if (previousColumn?.getColId() === "indicatorColumn") {
          console.log("ArrowLeft blocked: Cannot move to indicator column.");
          gridEvent.preventDefault();
        }
      }
    }
    if (key === "Enter") {
      // if (isProcessingRef.current) return;

      api.stopEditing();
      api.tabToNextCell(); // 셀만 이동
    }
  };

  //#region 2025.01.14 그리드 팝업관련 추가
  const handleCellDoubleClick = async (params: any) => {
    const rowData = params.api.getRowNode(params.node.id);
    const column = params.colDef;
    if (!column.editable) return;
    const columnfield = column.field;
    const columndisplay = column.cd_displaycolumn;
    const columnValue = params.value;
    const selectedSingleSetting = singlepopups.filter(
      (popup: any) => popup.cd_column === column.field
    );
    const selectedMultiSetting = multipopups.filter(
      (popup: any) => popup.cd_column === column.field
    );
    if (selectedSingleSetting.length > 0) {
      if (shouldSkipCellAction(params)) return;
      if (selectedSingleSetting[0].cd_popup) {
        if (selectedSingleSetting[0].cd_popup === "POPUP_PASSWORD") {
          params.api.stopEditing();

          const passwordPopup = <PasswordPopup />;
          const result = await showPopupModal({
            title: "비밀번호 변경",
            children: passwordPopup,
            width: "300px",
            height: "275px",
          });

          if (result !== undefined) {
            const rowNode = params.api.getRowNode(params.node.id);
            if (rowNode) {
              rowNode.setDataValue(columnfield, result); // 입력된 비밀번호 값을 해당 셀에 적용
            }
          }

          params.api.tabToNextCell();
          return; // 비밀번호 로직만 처리하고 빠져나감
        }

        params.api.stopEditing();
        const popupData = getSearchData(selectedSingleSetting[0].cd_popup, "");
        const modalData = {
          title: popupData.title,
          code: selectedSingleSetting[0].cd_popup,
          formData: popupData.selectedData,
        };
        const singlePopup = (
          <SinglePopup
            code={selectedSingleSetting[0].cd_popup}
            selectedData={popupData.selectedData}
            autoSearch={popupData.autoSearch}
          />
        );
        const result = await showPopupModal({
          title: popupData.title,
          children: singlePopup,
        });
        const popupSetting = PopupSettings[modalData.code];
        const { columns } = popupSetting;
        if (result) {
          const resultArr = result[0];
          const transformedData = {
            code: resultArr[columns[0]], // 예: resultArr['cd_biz']
            name: resultArr[columns[1]], // 예: resultArr['nm_wh']
            ...resultArr, // 나머지 값을 그대로 추가
          };
          if (params.node.id !== undefined) {
            const rowNode = params.api.getRowNode(params.node.id); // 특정 행의 노드 찾기
            if (rowNode) {
              if (rowNode.data) {
                // 임시방편
                if (columnfield === "cd_wh_ref") {
                  rowNode.setDataValue("cd_wh_ref", transformedData.code);
                  rowNode.setDataValue("nm_wh_ref", transformedData.name);
                } else {
                  columns.forEach((column: any) => {
                    if (memoizedColumnConfig)
                      if (
                        memoizedColumnConfig.some(
                          (config) => config.cd_column === column
                        )
                      ) {
                        rowNode.setDataValue(column, resultArr[column]);
                      }
                  });
                }
              }
            }
          }
          params.api.tabToNextCell();
        } else if (result === undefined) {
          if (params.node.id !== undefined) {
            const rowNode = params.api.getRowNode(params.node.id);
            if (rowNode && rowNode.data) {
              columns.forEach((column: any) => {
                if (memoizedColumnConfig)
                  if (
                    memoizedColumnConfig.some(
                      (config) => config.cd_column === column
                    )
                  ) {
                    rowNode.setDataValue(column, "");
                  }
              });
            }
          }
        }
      }
    } else if (selectedMultiSetting.length > 0) {
      if (shouldSkipCellAction(params)) return;
      if (selectedMultiSetting[0].cd_popup) {
        params.api.stopEditing();
        const popupData = getSearchData(selectedMultiSetting[0].cd_popup, "");
        const modalData = {
          title: popupData.title,
          code: selectedMultiSetting[0].cd_popup,
          formData: popupData.selectedData,
        };
        const multiPopup = (
          <MultiPopup
            code={selectedMultiSetting[0].cd_popup}
            selectedData={popupData.selectedData}
            autoSearch={popupData.autoSearch}
          />
        );
        const result = await showPopupModal({
          title: popupData.title,
          children: multiPopup,
        });
        const popupSetting = PopupSettings[modalData.code];
        const { columns } = popupSetting;
        if (result && result.length > 0) {
          if (params.node.id !== undefined) {
            const rowNode = params.api.getRowNode(params.node.id);
            if (rowNode && rowNode.data) {
              const newRows: any[] = [];
              result.forEach((selectedItem: any) => {
                if (selectedItem === result[0]) {
                  columns.forEach((col: any) => {
                    if (memoizedColumnConfig) {
                      if (
                        memoizedColumnConfig.some(
                          (config) => config.cd_column === col
                        )
                      ) {
                        rowNode.setDataValue(col, selectedItem[col]);
                      }
                    }
                  });
                } else {
                  const newRow: Record<any, any> = {};
                  columns.forEach((col: any) => {
                    newRow[col] = selectedItem[col];
                  });
                  newRows.push(newRow);
                }
              });
              if (newRows.length > 0) {
                newRows.forEach((row) => {
                  addRow(row);
                  const addedRow = row;
                });
              }
            }
          }
          params.api.tabToNextCell();
        } else if (result === undefined) {
          if (params.node.id !== undefined) {
            const rowNode = params.api.getRowNode(params.node.id);
            if (rowNode && rowNode.data) {
              columns.forEach((col: any) => {
                if (memoizedColumnConfig)
                  if (
                    memoizedColumnConfig.some(
                      (config) => config.cd_column === col
                    )
                  ) {
                    rowNode.setDataValue(col, "");
                  }
              });
            }
          }
        }
      }
    }
    if (column.yn_drilldown === "Y") {
      const params = {
        id: "com_CommonMapper_10",
        params: {
          cd_firm: userInfo.cd_firm,
          no_docu: columnValue,
        },
      };
      const result = await fnGetDataSet(params);

      const menuList = menuItems;
      if (menuInfo) {
        const filePath =
          `./${menuInfo.cd_module}/${result[0][0].cd_menu}/${result[0][0].cd_menu}`.toUpperCase();
        const drillDown = menuList
          ? menuList.find((item: any) => item?.cd_menu === result[0][0].cd_menu)
          : undefined;

        if (drillDown) {
          (window as any).onActivate(
            drillDown.cd_menu, // 메뉴 키
            drillDown.nm_menu, // 메뉴 제목
            drillDown.cd_module, // 모듈
            filePath, // 파일 경로
            drillDown, // 데이터
            drillDown.nm_path, // 파일경로 한글화
            rowData.data,
            "fg_drilldown.Search"
          );
        }
      }
    }
  };

  const singlepopupsRef = useRef(singlepopups);
  const multipopupsRef = useRef(multipopups);

  useEffect(() => {
    singlepopupsRef.current = singlepopups;
  }, [singlepopups]);
  useEffect(() => {
    multipopupsRef.current = multipopups;
  }, [multipopups]);

  //#endregion

  const selectAndFocusRow = (index: number) => {
    if (!gridRef.current?.api) {
      console.warn("Grid API is not available.");
      return;
    }

    const api = gridRef.current.api;

    // 1. 기존 포커스와 선택 초기화
    api.clearFocusedCell();
    api.deselectAll(); // 기존 선택된 행 초기화

    // 행 인덱스를 확인하여 범위 내인지 체크
    const totalRowCount = api.getDisplayedRowCount();
    if (index < 0 || index >= totalRowCount) {
      console.warn(
        `Index ${index} is out of range. Total rows: ${totalRowCount}`
      );
      return;
    }

    // 해당 인덱스의 행 노드를 가져오기
    const rowNode = api.getDisplayedRowAtIndex(index);
    if (rowNode) {
      // 행 선택
      rowNode.setSelected(true);

      // 해당 행이 화면에 보이도록 스크롤
      api.ensureIndexVisible(index);

      // 특정 셀에 포커스 (첫 번째 수정 가능한 컬럼)
      const firstEditableCol = resolvedColumnDefs.find(
        (col) => col.editable
      )?.field;
      if (firstEditableCol) {
        api.setFocusedCell(index, firstEditableCol);
      }
    } else {
      console.warn(`Row node at index ${index} is not found.`);
    }
  };

  // 필더링된 행만 전체 선택하는 함수
  const selectAllFiltered = () => {
    if (!gridRef.current?.api) {
      console.warn("Grid API is not available.");
      return;
    }

    const api = gridRef.current.api;

    // 1. 기존 포커스와 선택 초기화
    api.clearFocusedCell();
    api.deselectAll(); // 기존 선택된 행 초기화

    // 2. 필터링된 rows 전체 선택
    api.selectAllFiltered();
  };

  const handleInternalonRowClicked = (event: any) => {
    if (onRowClicked) {
      onRowClicked(event); // 외부 핸들러 호출
    }
  };

  const handleInternalCellValueChanged = async (event: any) => {
    if (onBeforeEdit && onBeforeEdit(event.data, event.colDef) === false) {
      return;
    }

    const currentFocusedCell = gridRef.current?.api.getFocusedCell(); // 현재 포커스된 셀 저장

    if (onCellValueChanged) {
      onCellValueChanged(event); // 외부 핸들러 호출
    }

    // const updatedRow = { ...event.data };
    // gridRef.current?.api.applyTransaction({ update: [updatedRow] });

    // 변경된 셀 강제 렌더링
    gridRef.current?.api.refreshCells({
      rowNodes: [event.node],
      force: true, // 강제 렌더링
    });

    const index = modifiedRows.current.added.findIndex(
      (row) => row.KeyField === event.data.KeyField
    );

    if (index !== -1) {
      // KeyField가 동일한 row를 Change로 변경
      modifiedRows.current.added[index] = event.data;
    } else {
      const index = modifiedRows.current.updated.findIndex(
        (row) => row.KeyField === event.data.KeyField
      );

      if (index !== -1) {
        // KeyField가 동일한 row를 Change로 변경
        modifiedRows.current.updated[index] = event.data;
      } else {
        // KeyField가 없는 경우 추가하는 로직 (선택 사항)
        modifiedRows.current.updated.push(event.data);
      }
    }

    computeFooterSummary();
  };

  //#endregion

  //#region getChange 변경된 데이터 가져오기
  // 2025.01.08 필수값 체크로직 추가
  const getChange = () => {
    return {
      modifiedRowcount:
        modifiedRows.current.added.length +
        modifiedRows.current.updated.length +
        modifiedRows.current.deleted.length,
      insert: modifiedRows.current.added,
      update: modifiedRows.current.updated,
      delete: modifiedRows.current.deleted,
    };
  };

  const requiredValuesCheck = () => {
    try {
      modifiedRows.current.added.map((change) => {
        Object.keys(requireds).map((required) => {
          if (
            (Object.hasOwn(change, required) &&
              (change[required] == null || change[required] == "")) ||
            !Object.hasOwn(change, required)
          ) {
            showDialog("alter", requireds[required] + "은 필수값입니다");
            throw "finish";
          }
        });
      });
      modifiedRows.current.updated.map((change) => {
        Object.keys(requireds).map((required) => {
          if (
            (Object.hasOwn(change, required) &&
              (change[required] == null || change[required] == "")) ||
            !Object.hasOwn(change, required)
          ) {
            showDialog("alter", requireds[required] + "은 필수값입니다");
            throw "finish";
          }
        });
      });
      return true;
    } catch (Exception) {
      if (Exception !== "finish") throw Exception;
      else return false;
    }
  };

  //#endregion

  //#region resetUpdate 저장 후 상태 초기화
  const resetUpdate = () => {
    modifiedRows.current = { added: [], updated: [], deleted: [] };
  };

  //#endregion

  //#region 하단 차트 관련 내용

  const chartContainerRef = useRef<HTMLDivElement>(null); // 독립 차트 컨테이너 참조
  const gridApiRef = useRef<any>(null); // AG Grid API
  const columnApiRef = useRef<any>(null); // AG Grid Column API

  const handleGridReady = (params: any) => {
    const { api, columnApi } = params;

    gridApiRef.current = params.api;
    columnApiRef.current = params.columnApi;

    setIsGridReady(true);

    if (onGridReady) {
      onGridReady(params); // 외부 콜백 호출
    }

    // 데이터가 렌더링된 후 기본 차트 생성
    if (
      enableExternalChart &&
      chartColumns.length > 0 &&
      chartContainerRef.current
    ) {
      try {
        api.createRangeChart({
          cellRange: {
            columns: chartColumns,
          },
          chartType: "groupedColumn", // 차트 유형
          chartContainer: chartContainerRef.current, // 독립 차트 컨테이너
        });
        console.log("Chart successfully created!");
      } catch (error) {
        console.error("Error creating chart:", error);
      }
    }
  };

  //#endregion

  //#region 필터 관련 내용

  // 필터를 적용하는 메서드
  const applyFilters = (filterConditions: { [key: string]: any }) => {
    if (gridRef.current?.api) {
      // 1. 작업 시작: 화면 동결
      gridRef.current.api.showLoadingOverlay();

      // 2. 필터링 작업 수행
      const filterModel: { [key: string]: any } = {};

      // filterConditions에 따라 필터 모델 생성

      Object.keys(filterConditions).forEach((field) => {
        const value = filterConditions[field];

        filterModel[field] =
          value === "" || value === undefined
            ? { type: "blank" } // 빈 값은 "blank"로 필터링
            : { type: "equals", filter: value }; // 특정 값은 "equals"로 필터링
      });

      console.log("Applying filters:", filterModel);

      gridRef.current.api.setFilterModel(filterModel);

      // 3. 필터 상태 반영
      gridRef.current.api.onFilterChanged();

      // const updatedRowData =
      //   gridRef.current?.api.getRenderedNodes().map((node) => node.data) || [];
      // calculateFooter(updatedRowData); // 변경된 데이터를 기반으로 합계 재계산

      computeFooterSummary();

      // 4. 작업 종료: 화면 활성화 (작업 완료 후 약간의 지연 추가)
      setTimeout(() => {
        gridRef.current?.api.hideOverlay();
      }, 500); // 500ms 지연 후 오버레이 제거
    }
  };

  // 필터를 초기화하는 메서드
  const resetFilters = () => {
    if (gridRef.current?.api) {
      // 1. 작업 시작: 화면 동결
      gridRef.current.api.showLoadingOverlay();

      // 2. 필터 초기화
      gridRef.current.api.setFilterModel(null);
      gridRef.current.api.onFilterChanged();

      // 3. 작업 종료: 화면 활성화
      setTimeout(() => {
        gridRef.current?.api.hideOverlay();
      }, 500); // 500ms 지연 후 오버레이 제거
    }
  };

  //#endregion

  //#region Indicator 관련 내용
  const columnDefsWithRowNumber = [
    // {
    //   headerName: "No.", // 순번 컬럼
    //   valueGetter: (params: { node: { rowPinned: any; rowIndex: number } }) => {
    //     if (params.node.rowPinned) {
    //       const visibleRowCount =
    //         gridRef.current?.api.getDisplayedRowCount() || 0;
    //       return visibleRowCount;
    //     }
    //     return params.node.rowIndex + 1; // 일반 행에서는 순번 표시
    //   },

    //   width: 70, // 너비
    //   pinned: "left", // 왼쪽 고정
    //   cellClass: "row-number-cell", // 스타일 클래스
    //   suppressMovable: true, // 이동 불가
    //   sortable: false, // 정렬 불가
    //   filter: false, // 필터링 불가
    // },
    ...resolvedColumnDefs, // 기존 컬럼 정의
  ];

  //#endregion

  //#region shareData 데이터 업데이트
  const sharedCellValueChanged = (data: any) => {
    const Change = { ...data };
    // setRowData((prev) =>
    //   prev.map((row) => (row.KeyField === Change.KeyField ? Change : row))
    // );

    const rowNode = gridRef.current?.api.getRowNode(Change.KeyField);
    if (rowNode) {
      Object.assign(rowNode.data, Change);
      gridRef.current?.api.refreshCells({ rowNodes: [rowNode] });
    }

    // gridRef.current?.api.applyTransaction({ update: [Change] });
    const focusedCell = gridRef.current?.api.getFocusedCell();
    if (focusedCell) {
      const rowNode = gridRef.current?.api.getRowNode(Change.KeyField);
      if (rowNode) {
        rowNode.setSelected(true);
      }
    }

    const index = modifiedRows.current.added.findIndex(
      (row) => row.KeyField === Change.KeyField
    );

    if (index !== -1) {
      // KeyField가 동일한 row를 Change로 변경
      modifiedRows.current.added[index] = Change;
    } else {
      const index = modifiedRows.current.updated.findIndex(
        (row) => row.KeyField === Change.KeyField
      );

      if (index !== -1) {
        // KeyField가 동일한 row를 Change로 변경
        modifiedRows.current.updated[index] = Change;
      } else {
        // KeyField가 없는 경우 추가하는 로직 (선택 사항)
        modifiedRows.current.updated.push(Change);
      }
    }

    // // 2. 합계 재계산
    computeFooterSummary();
  };

  const suppressEventDispatchRef = useRef(false); // 이벤트 억제 상태

  // 이벤트 억제 활성화
  const suppressEventDispatch = () => {
    suppressEventDispatchRef.current = true;
  };

  // 이벤트 억제 비활성화
  const allowEventDispatch = () => {
    suppressEventDispatchRef.current = false;
  };

  // 현재 이벤트 디스패치 가능 여부 확인
  const shouldDispatchEvent = () => !suppressEventDispatchRef.current;

  const setValue = (rowNode: RowNode, column: string, value: any) => {
    const gridApi = gridRef.current?.api;

    if (!gridApi || !rowNode) return;

    const colDef = gridApi.getColumnDef(column);
    if (!colDef) {
      console.warn(`Column '${column}' does not exist.`);
      return;
    }
    const oldValue = rowNode.data[column];

    // 값이 다르거나 oldValue가 null/undefined인 경우 업데이트
    if (oldValue !== value || oldValue == null) {
      // 값 업데이트
      rowNode.setDataValue(column, value);
    }
  };

  // 드릴다운으로 할 때 column 존재 여부 체크하여 setDataValue
  const setValueDrill = (rowNode: RowNode, column: string, value: any) => {
    const gridApi = gridRef.current?.api;

    if (!gridApi || !rowNode) return;

    const colDef = gridApi.getColumnDef(column);
    if (!colDef) {
      console.warn(`Column '${column}' does not exist.`);
      return;
    }
    const oldValue = rowNode.data[column];

    // 값이 다르거나 oldValue가 null/undefined인 경우 업데이트
    if (oldValue !== value || oldValue == null) {
      // 값 업데이트
      rowNode.setDataValue(column, value);
    }
  };

  const getFlatDataFromGrid = (
    columnKeys: string[]
  ): Map<string, FlatRowType> => {
    const flatRowMap = new Map<string, FlatRowType>();

    gridRef.current?.api.forEachNodeAfterFilterAndSort((node) => {
      const raw = node.data;
      const flat: FlatRowType = {};

      columnKeys.forEach((key) => {
        let val = raw[key];

        if (typeof val === "object" && val !== null) {
          val = val.label ?? val.text ?? val.value ?? JSON.stringify(val);
        }

        if (isDateValue(val)) {
          flat[key] = {
            value:
              val.length === 8
                ? `${val.slice(0, 4)}/${val.slice(4, 6)}/${val.slice(6, 8)}`
                : val,
            dataType: "dateTime" as const,
          };
        }
        //  앞에 0이 붙은 코드 → 먼저 처리
        else if (
          typeof val === "string" &&
          /^\d+$/.test(val) &&
          val.startsWith("0")
        ) {
          flat[key] = {
            value: `'${val}`, // 또는 '=' + '"' + val + '"'
            dataType: "string",
          };
        } else if (isNumeric(val)) {
          flat[key] = {
            value: Number(val),
            dataType: "number" as const,
          };
        } else {
          //  앞자리 0 보존을 위한 처리

          flat[key] = {
            value: val ?? "",
            dataType: "string" as const,
          };
        }
      });

      flatRowMap.set(node.id!, flat);
    });

    return flatRowMap;
  };

  //#endregion

  //#region 외부에서 접근 가능한 메서드 정의
  useImperativeHandle(ref, () => ({
    api: gridApiRef.current, // AG Grid API 노출
    columnApi: columnApiRef.current, // AG Grid Column API 노출
    selectAndFocusRow,
    selectAllFiltered,
    saveColumnState,
    restoreColumnState,
    getSelectedRow,
    getSelectedRows,
    onBeforeAddRow,
    addRow,
    deleteSelectedRows,
    deleteRowByIndex,
    deleteAllRows,
    getChange,
    requiredValuesCheck,
    resetUpdate,
    footer: getFooterData(),
    getFooterDataAsync,
    applyFilters,
    resetFilters,
    sharedCellValueChanged,
    suppressEventDispatch,
    allowEventDispatch,
    setValue: (rowNode: RowNode, column: string, value: any) =>
      setValue(rowNode, column, value),
    setValueDrill: (rowNode: RowNode, column: string, value: any) =>
      setValueDrill(rowNode, column, value),
    getFlatDataFromGrid,
    // 여기는 트리
    saveExpandState,
    restoreExpandState,
  }));

  //#endregion

  // RowData 업데이트 후 포커스를 재설정하는 함수
  const handleRowDataUpdated = () => {
    if (gridRef.current?.api) {
      const lastSelectedNode = gridRef.current.api.getSelectedNodes()[0]; // 마지막 선택된 행 노드
      if (lastSelectedNode) {
        const rowIndex = lastSelectedNode.rowIndex ?? 0; // null 또는 undefined 시 0으로 설정
        // columnDefs가 존재할 때만 find 실행
        const firstEditableCol = columnDefs?.find(
          (col: ColDef) => col.editable
        )?.field; // 첫 번째 수정 가능한 컬럼

        if (firstEditableCol) {
          // 포커스 재설정
          gridRef.current.api.setFocusedCell(rowIndex, firstEditableCol);
        }
      }
    }
  };

  // 메뉴마다 우클릭했을 때 popmenu가 그리드별로 다르기에 seq를 추가함. 메뉴에서 그리드에 추가해주면됨
  // sys_popmenu 테이블에 no_seq_dw임
  const { seq } = props;
  const menuItemsRef = useRef<
    Array<Array<{ name: string; action: () => void }>>
  >([]);
  // 우클릭 했을 때 커스텀을 하려했는데 비동기작업이 안된다고함.
  // 그래서 메뉴페이지 열때 값을 가져와 만들어 두고 그걸 사용하려고 추가했음.
  // 더 좋은 방법이 있다면 수정하셔도 되고 말해주셔도 되고 좋습니다.
  useEffect(() => {
    const fetchMenuItems = async () => {
      const fileName = props.menuInfo?.cd_menu;
      if (!fileName) {
        console.log("fileName is missing or empty, skipping fetchMenuItems");
        return;
      }
      const seqs = Array.isArray(props.seq)
        ? props.seq.filter((seq) => seq !== undefined)
        : [props.seq].filter((seq) => seq !== undefined);
      let allMenuItems: any[] = [];
      try {
        const results = await Promise.all(
          seqs.map(async (seq: number, index: number) => {
            const params = {
              id: "com_CommonMapper_09",
              params: {
                cd_menu: fileName,
                cd_firm: userInfo.cd_firm,
                no_seq_dw: seq,
              },
            };
            const data: any = await fnGetDataSet(params);
            if (Array.isArray(data) && data.length > 0) {
              const menuItems: { name: string; action: () => void }[] = [];
              for (let i = 0; i < data[0].length; i++) {
                const item = data[0][i];
                const menuName: string =
                  item?.nm_popmenu || item?.cd_popmenu || "";
                menuItems.push({
                  name: menuName,
                  action: () => {
                    console.log(`${menuName} clicked`);
                  },
                });
              }
              menuItemsRef.current[seq] = menuItems;
              return menuItems;
            }
            return [];
          })
        );
        allMenuItems = allMenuItems.concat(...results);
        menuItemsRef.current = allMenuItems;
      } catch (error) {
        console.error("Error fetching context menu data:", error);
      }
    };
    if (props.menuInfo?.cd_menu) {
      fetchMenuItems();
    }
  }, []);

  const gridOptionsExt = {
    ...props.gridOptions,
    suppressScrollOnNewData: true, // 데이터 업데이트 시 스크롤 방지
    suppressColumnVirtualisation: true, // 가상화 비활성화
    // 우클릭 메뉴 만들어 사용하기인데 디폴드메뉴가 있으면 합쳐서 보여주는거임.
    getContextMenuItems: (params: any) => {
      const today = new Date();
      const formattedDate = today.toISOString().slice(0, 10).replace(/-/g, "");
      const formattedTime = today.toTimeString().slice(0, 8).replace(/:/g, "");

      const defaultItems = Array.isArray(params.defaultItems)
        ? params.defaultItems
        : [];
      if (defaultItems.length === 0) {
        return [];
      }
      if (defaultItems.length > 6) {
        defaultItems[6] = {
          name: "내보내기",
          subMenu: [
            {
              name: "CSV로 내보내기",
              action: () =>
                gridRef.current!.api.exportDataAsExcel({
                  fileName: `${menuInfo?.nm_menu}[${formattedDate}${formattedTime}].csv`,
                  sheetName: "다운로드",
                }),
            },
            {
              name: "Excel로 내보내기",
              action: () =>
                gridRef.current!.api.exportDataAsExcel({
                  fileName: `${menuInfo?.nm_menu}[${formattedDate}${formattedTime}].xlsx`,
                  sheetName: "다운로드",
                }),
            },
          ],
        };
      }
      const customMenuItems = menuItemsRef.current.map((item: any) => {
        // 구분선인지 확인
        if (item.name === "-") {
          return "separator";
        }
        // 일반 메뉴 항목
        return {
          name: item.name,
          action: item.action,
        };
      });
      return [...defaultItems, "separator", ...customMenuItems];
    },
  };

  const handleContextMenu = (params: any) => {
    const { rowIndex } = params; // 우클릭한 행의 인덱스
    if (rowIndex) {
      selectAndFocusRow(rowIndex);
    }

    if (onContextMenu) {
      onContextMenu(params); // 부모 컴포넌트에서 받은 이벤트 실행
    }
  };
  // 2025.01.15 우클릭 크기 그리드 고정제외
  const popupParent = useMemo<HTMLElement | null>(() => {
    return document.querySelector("body");
  }, []);

  const handleOnRowDoubleClicked = (event: any) => {
    console.log("AgGridReact event", event);
    if (onRowDoubleClicked) {
      onRowDoubleClicked(event);
    }
  };

  const handleonCellDoubleClicked = (event: any) => {
    console.log("AgGridReact vent", event);
    if (onCellDoubleClicked) {
      onCellDoubleClicked(event);
    }
  };

  const handleRowDragEnd = () => {
    if (gridRef.current?.api) {
      // const updatedData = gridRef.current.api.getDisplayedRowAtIndex(0)!.data;
      const allData: RowData[] = [];

      gridRef.current.api.forEachNode((node, index) => {
        if (node.data) {
          allData.push({ ...node.data, no_seq_sort: index + 1 });
        }
      });

      setRowData(allData);
    }
  };

  const handleInternalSelectionChanged = (
    gridRef: any,
    onSelectionChanged?: (selectedRows: any[]) => void
  ) => {
    if (gridRef?.current?.api && onSelectionChanged) {
      const selectedRows = gridRef.current.api.getSelectedRows(); //  선택된 행 가져오기
      onSelectionChanged(selectedRows); //  부모 컴포넌트로 전달
    }
  };

  let savedExpandState: Record<string, boolean> | null = null; // 상태를 저장할 타입

  const getGridExpandKey = () => {
    // debugger;
    const baseKey = menuInfo?.cd_menu; // Current page path

    const componentId = cdGrid || "defaultGrid"; // gridId가 없으면 기본값 사용
    return `gridexpandState_${baseKey}_${componentId}`;
  };

  /// Tree Expand 상태 저장
  const saveExpandState = () => {
    if (!gridApiRef.current) {
      return;
    }

    const expandState: Record<string, boolean> = {};

    gridApiRef.current.forEachNode((node: RowNode) => {
      if (node.group) {
        expandState[node.key!] = node.expanded ?? false;
      }
    });

    localStorage.setItem(getGridExpandKey(), JSON.stringify(expandState));

    console.log("saveExpandState:", expandState);
  };

  // Tree Expand 상태 복원
  const restoreExpandState = () => {
    if (!gridApiRef.current) {
      return;
    }

    const savedState = localStorage.getItem(getGridExpandKey());
    if (!savedState) {
      return;
    }

    const savedExpandState: Record<string, boolean> = JSON.parse(savedState);

    gridApiRef.current.forEachNode((node: RowNode) => {
      if (
        node.group &&
        node.key !== null &&
        node.key !== undefined &&
        savedExpandState[node.key] !== undefined
      ) {
        node.setExpanded(savedExpandState[node.key]);
      }
    });

    console.log("restoreExpandState:", savedExpandState);
  };

  const renderGridContent = () => {
    return (
      <div
        className="snGrid"
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* 그리드 */}
        <div className="ag-theme-alpine" style={{ flex: "1 1 auto", ...style }}>
          <AgGridReact
            rowStyle={{ fontSize: StyleSettings.font }}
            containerStyle={{ fontSize: StyleSettings.font }}
            gridOptions={gridOptionsExt}
            ref={gridRef}
            columnDefs={columnDefsWithRowNumber}
            rowData={rowData}
            // pinnedBottomRowData={pinnedBottomRowData} // 트리는 풋터 필요없음
            defaultColDef={defaultColDef}
            pagination={pagination}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            enableRangeSelection={true}
            animateRows={animateRows}
            rowDragManaged={rowDragManaged}
            suppressMoveWhenRowDragging={suppressMoveWhenRowDragging}
            cellSelection={cellSelection}
            suppressNoRowsOverlay={suppressNoRowsOverlay}
            suppressMenuHide={suppressMenuHide}
            rowGroupPanelShow={rowGroupPanelShow}
            enableCharts={enableExternalChart}
            localeText={localeText}
            onGridReady={handleGridReady}
            rowSelection={rowSelection}
            onRowClicked={handleInternalonRowClicked}
            onRowDoubleClicked={handleOnRowDoubleClicked}
            // onCellDoubleClicked={handleonCellDoubleClicked}
            onCellDoubleClicked={handleCellDoubleClick}
            onCellValueChanged={handleInternalCellValueChanged}
            domLayout="normal"
            suppressHorizontalScroll={false}
            suppressRowVirtualisation={false}
            onCellFocused={handleCellFocused} //포커스 변경 후 데이터 처리
            onCellKeyDown={handleCellKeyDown} // 키보드 입력 처리
            popupParent={popupParent} // 우클릭 크기 그리드 고정 제외
            suppressRowClickSelection={false}
            suppressFocusAfterRefresh={true}
            suppressCellFocus={false}
            // suppressRowTransform={true} //CSS Transform 애니메이션 비활성화
            onColumnMoved={handleColumnStateChange}
            onColumnResized={handleColumnStateChange}
            onColumnPinned={handleColumnStateChange}
            onColumnVisible={handleColumnStateChange}
            onSelectionChanged={() =>
              handleInternalSelectionChanged(ref, onSelectionChanged)
            } //  추가
            headerHeight={headerHeight}
            getRowHeight={() => {
              return 40;
            }} // cell, footer 높이 설정
            {...gridOptions}
            // 트리 사용으로 추가
            loading={loading}
            treeData={treeData}
            getDataPath={getDataPath}
            groupDefaultExpanded={groupDefaultExpanded}
            autoGroupColumnDef={mergedAutoGroupColumnDef}
            getRowId={(params) => params.data.KeyField} // 트리구조에선 getRowId를 설정해줘야 함
          />
        </div>

        {/* 독립 차트 */}
        {enableExternalChart && (
          <div
            ref={chartContainerRef}
            style={{
              marginTop: "20px",
              height: "300px",
              width: "100%",
              border: "1px solid #ccc",
            }}
          >
            {/* 독립 차트가 렌더링됩니다 */}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {title ? (
        <SnTab
          titles={[title]}
          width={"100%"}
          header={[
            <SnTabHeader
              onBtnAddClick={onBtnAddClick}
              onBtnDeleteClick={onBtnDeleteClick}
              body={header}
            />,
          ]}
          body={[renderGridContent()]}
        />
      ) : (
        renderGridContent()
      )}
    </>
  );
});
export default React.memo(SnTree);
